/**
 * MapAccessDenied Component
 * Hiển thị khi user không có quyền xem bản đồ restricted
 * Hỗ trợ 2 levels: internal (cần đăng nhập) và private (chỉ leader/admin/ceo)
 */
import { Link } from 'react-router-dom'
import { HiOutlineLockClosed, HiOutlineLogin, HiOutlineMap, HiOutlineShieldCheck } from 'react-icons/hi'

function MapAccessDenied({ onGoBack, accessLevel = 'internal', message }) {
  const isPrivate = accessLevel === 'private'

  // Different styles based on access level
  const gradientClass = isPrivate
    ? 'from-red-500 to-rose-600'
    : 'from-amber-500 to-orange-500'

  const title = isPrivate ? 'Bản đồ riêng tư' : 'Bản đồ nội bộ'
  const defaultMessage = isPrivate
    ? 'Bản đồ này chỉ dành cho Leader, Admin và CEO. Bạn không có quyền truy cập.'
    : 'Bản đồ này chỉ dành cho thành viên đã đăng nhập. Vui lòng đăng nhập để xem nội dung.'

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-md w-full">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header with icon */}
          <div className={`bg-gradient-to-r ${gradientClass} px-6 py-8 text-center`}>
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm mb-4">
              {isPrivate ? (
                <HiOutlineShieldCheck className="w-10 h-10 text-white" />
              ) : (
                <HiOutlineLockClosed className="w-10 h-10 text-white" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-white">
              {title}
            </h1>
          </div>

          {/* Content */}
          <div className="px-6 py-8 text-center">
            <p className="text-gray-600 mb-6 leading-relaxed">
              {message || defaultMessage}
            </p>

            {/* Actions */}
            <div className="space-y-3">
              {!isPrivate && (
                <Link
                  to="/login"
                  className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg shadow-primary-500/25"
                >
                  <HiOutlineLogin className="w-5 h-5" />
                  Đăng nhập
                </Link>
              )}

              <button
                onClick={onGoBack}
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
              >
                <HiOutlineMap className="w-5 h-5" />
                Quay về trang bản đồ
              </button>
            </div>
          </div>

          {/* Footer hint - only show for internal (not private) */}
          {!isPrivate && (
            <div className="px-6 pb-6">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-800 text-center">
                  <span className="font-medium">Chưa có tài khoản?</span>{' '}
                  <Link to="/register" className="text-amber-600 hover:text-amber-700 underline">
                    Đăng ký ngay
                  </Link>
                </p>
              </div>
            </div>
          )}

          {/* Footer hint for private maps */}
          {isPrivate && (
            <div className="px-6 pb-6">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-700 text-center">
                  Nếu bạn cần truy cập, vui lòng liên hệ quản trị viên
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Decorative elements */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-400">
            Đất Xanh Miền Trung - Công cụ Marketing BĐS
          </p>
        </div>
      </div>
    </div>
  )
}

export default MapAccessDenied
